import { configureStore } from '@reduxjs/toolkit';
import inputReducer, { includeActions } from 'store/reducers/inputReducer';
import uiReducer from 'store/reducers/uiReducer';
import libraryReducer from 'store/reducers/libraryReducer';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';

import { persistStore, persistReducer, createTransform, createMigrate } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {DEFAULT_NOTIFICATIONS, DEFAULT_OPTIMIZATION} from 'store/reducers/constants/constants.js'
import { migrations } from 'store/persistMigrations.js'

const transformer = createTransform(
  // outbound: state → storage (strip large/transient data to stay within localStorage quota)
  (outboundState, key) => {
    if (key === 'present') {
      // eslint-disable-next-line unused-imports/no-unused-vars
      const { geoJson, notifications, loading, optimization, ...rest } = outboundState;
      return { ...rest, geoJson, notifications: DEFAULT_NOTIFICATIONS, loading: {}, optimization: DEFAULT_OPTIMIZATION };
    }
    return outboundState;
  },
);

/*
Persisted-state schema version.

This config had no `version`/`migrate`, so `autoMergeLevel2` merged whatever a
returning visitor had in localStorage into the current defaults — for as long
as the app has existed. Any release that changed the shape of persisted state
therefore shipped straight into existing browsers, and a mismatch surfaces as a
crash on the model pages (the only screens that read deeply into that state).
That is exactly what happened after the #113 promotion: the optimizers threw
into the ErrorBoundary for returning visitors while incognito windows, which
have no stored state, worked fine.

Bumping the version discards pre-versioned state once, per browser, on the next
load; after that the stored blob carries `_persist.version` and persistence
behaves normally. Discarding rather than mapping fields is deliberate: we do not
know every shape change that accumulated while this was unversioned, and an
incorrect migration would fail the same way the bug does.

Cost of the discard: an unsaved, in-progress scenario in a returning browser is
lost. Saved scenarios are server-side (fetchSavedScenario / saveScenario) and are
unaffected. Losing unsaved local work once is strictly better than a page that
cannot open at all.

RAISE THIS NUMBER whenever the shape of persisted state changes — that is the
whole point of it, and it is now cheap to do. Add the matching step to
persistMigrations.js; a step may map the state forward (as 2 does) instead of
discarding it.
*/
export const PERSIST_VERSION = 3;

// The steps themselves live in persistMigrations.js, free of this module's side
// effects, so the tests exercise the real ones rather than a copy.

const persistConfig = {
  key: 'root',
  storage: storage,
  stateReconciler: autoMergeLevel2,
  transforms: [transformer],
  blacklist: ['past', 'future'],  // Don't persist undo/redo history — it re-creates on actions
  /*
  NO `throttle` here, deliberately — it was tried and reverted.

  The churn is real: without a throttle redux-persist JSON.stringifies the whole
  `present` on every dispatch, and `present` includes geoJson and the
  O(locations²) travel-cost matrix, because the transform above does NOT strip
  them (it destructures geoJson out and then puts it straight back). While a
  solve is polling that is a multi-megabyte string built every 750ms-5s and
  immediately discarded.

  But `throttle: 1000` delays the write-back of the migrated, version-stamped
  state — and e2e/smoke.spec.js "an optimizer opens for a user carrying
  pre-versioning localStorage" caught it. That assertion is not incidental: if
  the stamp is not written back, the legacy blob is re-migrated on EVERY load
  instead of once, and stale persisted state crashing every optimizer for
  returning visitors is exactly the 2026-08-17 production outage the version
  guard exists to prevent.

  Trading a guard against a known outage for a GC optimisation is the wrong way
  round. Doing this properly means flushing the persistor as soon as migration
  completes, then throttling steady-state writes — a change that deserves its
  own PR and its own test pinning the once-only reset, not a rider on this one.
  */
  version: PERSIST_VERSION,
  migrate: createMigrate(migrations, { debug: false }),
};

/*
This records action TYPES, not actions.

It used to push the whole action object. The cap of 10 (each side) made that look
bounded, but the size was not: the actions passing through here include
`posts/fetchRunResults/fulfilled`, which carries every route path and the full
stop data, and `inputData/fetchTravelCosts/fulfilled`, which carries the entire
O(locations²) cost matrix. These are independent copies — nothing is shared with
Redux state — so the app pinned up to twenty full solve payloads in a closure
that outlives every component.

All of it existed to answer one boolean: selectLastActionMapRelated asks whether
the last action's type contains 'addRowFromMap' (mapSelectors.js). A string is
the whole requirement.
*/
const historyMiddleware = () => {
  let past = [];
  let future = [];
  const MAX_SIZE = 10;

  // Expose history through the API. These stay arrays of type strings, so the
  // shape callers see is unchanged apart from holding strings.
  historyAPI.getPast = () => [...past];
  historyAPI.getFuture = () => [...future];
  /*
  Copy-free accessor for the only consumer. The getters above spread on every
  call, and that call sits inside a useSelector — so they allocated a fresh
  array on every dispatch, which is the churn this codebase has OOM'd on.

  Past is a stack and future is a queue: undo pops the END of past onto the
  FRONT of future, and redo shifts that front back off. So the action a button
  would act on is past[last] but future[0]. That asymmetry is load-bearing (it
  was the map-undo-flicker bug) and is preserved here.
  */
  historyAPI.getNextType = (lookBackwards = true) => {
    if (lookBackwards) return past.length ? past[past.length - 1] : null;
    return future.length ? future[0] : null;
  };

  const applyUndo = () => {
    if (past.length > 0) {
      future.unshift(past.pop());
      if (future.length > MAX_SIZE) future.pop();
    }
  };

  const applyRedo = () => {
    if (future.length > 0) {
      past.push(future.shift());
      if (past.length > MAX_SIZE) past.shift();
    }
  };

  /*
  Mirrors redux-undo's own filter. This list used to record EVERY action, which
  made it a different history from the one Undo actually walks — and the map's
  undo/redo buttons read this one to decide whether the next undo is a map
  action. Placing a location showed them for a few hundred milliseconds and then
  any unrelated dispatch (a solve-status poll is enough) pushed a non-map action
  on top and they vanished. Recording only undoable actions makes "the last
  entry here" mean "the action the next Undo will revert", which is the question
  the buttons are actually asking.
  */
  const recordAction = (action) => {
    if (!includeActions.includes(action.type)) return;
    // The TYPE only — see the note above this middleware. The allowlist does not
    // help here: the actions on it are precisely the heavy ones (setScenario,
    // fetchRunResults/fulfilled, fetchTravelCosts/fulfilled).
    past.push(action.type);
    if (past.length > MAX_SIZE) past.shift();
    future = [];
  };

  return (_store) => (next) => (action) => {
    if (action.type.includes("getAddress")) return next(action);
    if (action.type.includes('UNDO')) applyUndo();
    else if (action.type.includes('REDO')) applyRedo();
    else if (action.type.includes('REHYDRATE')) { past = []; future = []; }
    else recordAction(action);
    return next(action);
  };
};

export const historyAPI = {
  getPast: () => [],
  getFuture: () => [],
};



const store = configureStore({
  reducer: {
    inputData: persistReducer(persistConfig, inputReducer),
    ui: uiReducer,
    // Account data, not scenario data: deliberately outside the persisted,
    // undoable inputData slice. Refetched per session rather than stored.
    library: libraryReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }).concat(historyMiddleware()),
});

export const persistor = persistStore(store);
// Do NOT purge here. persistor.purge() wipes the persisted scenario from storage
// on every boot, which erased the user's in-progress scenario on reload / sign-in
// even though persistence is fully configured above. It was a debugging leftover
// (uncommented in a WIP "Checkpoint" commit); persisting the scenario is intended.
export default store;
